import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Activity, LogIn, UserPlus, Package, FileUp, BarChart3,
  Factory, ChevronLeft, ChevronRight, RefreshCw, Search
} from 'lucide-react';

const actionConfig = {
  login: { icon: LogIn, bg: 'bg-blue-50', text: 'text-blue-700', label: 'Login' },
  user_created: { icon: UserPlus, bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'User Created' },
  order_created: { icon: Package, bg: 'bg-amber-50', text: 'text-amber-700', label: 'Order Created' },
  order_update: { icon: Package, bg: 'bg-indigo-50', text: 'text-indigo-700', label: 'Order Update' },
  order_lifecycle: { icon: Package, bg: 'bg-purple-50', text: 'text-purple-700', label: 'Lifecycle Change' },
  dpr_update: { icon: BarChart3, bg: 'bg-orange-50', text: 'text-orange-700', label: 'DPR Update' },
  file_upload: { icon: FileUp, bg: 'bg-teal-50', text: 'text-teal-700', label: 'File Upload' },
  factory_created: { icon: Factory, bg: 'bg-cyan-50', text: 'text-cyan-700', label: 'Factory Created' },
};

const PAGE_SIZE = 30;

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterUser, setFilterUser] = useState('all');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/activity-logs?limit=200&skip=0`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) { toast.error('Failed to load activity logs'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Get unique users for filter dropdown
  const uniqueUsers = [...new Map(logs.map(l => [l.user_id, { id: l.user_id, name: l.user_name }])).values()];

  // Filter logs
  const filteredLogs = logs.filter(l => {
    if (filterUser !== 'all' && l.user_id !== filterUser) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        l.user_name?.toLowerCase().includes(q) ||
        l.details?.toLowerCase().includes(q) ||
        l.action?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Paginate filtered results
  const paginatedLogs = filteredLogs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredLogs.length / PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [searchQuery, filterUser]);

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) +
      ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <DashboardLayout>
      <div data-testid="activity-logs-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-[#0A0A0A] tracking-tight">Activity Logs</h1>
            <p className="text-sm text-[#71717A] mt-1">{filteredLogs.length} of {total} events</p>
          </div>
          <Button variant="outline" onClick={fetchLogs} className="rounded-md text-sm border-black/[0.12]" data-testid="refresh-logs-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              placeholder="Search by user, action, or details..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 rounded-md border-black/[0.08]"
              data-testid="logs-search-input"
            />
          </div>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-[200px] h-10 rounded-md border-black/[0.08]" data-testid="filter-user-select">
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {uniqueUsers.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : paginatedLogs.length === 0 ? (
            <div className="text-center py-20">
              <Activity className="w-10 h-10 text-[#D4D4D8] mx-auto mb-3" />
              <p className="text-sm text-[#A1A1AA]">{searchQuery || filterUser !== 'all' ? 'No logs match the filters' : 'No activity recorded yet'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] border-b border-black/[0.06]">
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">User</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Action</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] hidden md:table-cell">Details</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedLogs.map((log) => {
                  const config = actionConfig[log.action] || { icon: Activity, bg: 'bg-gray-50', text: 'text-gray-700', label: log.action };
                  const Icon = config.icon;
                  return (
                    <TableRow key={log.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`log-row-${log.id}`}>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium text-[#0A0A0A]">{log.user_name}</p>
                          <p className="text-[10px] text-[#A1A1AA] capitalize">{log.user_role?.replace('_', ' ')}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                            <Icon className={`w-3 h-3 ${config.text}`} />
                          </div>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${config.bg} ${config.text}`}>
                            {config.label}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <p className="text-sm text-[#52525B] max-w-[400px] truncate">{log.details}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-xs text-[#71717A] whitespace-nowrap">{formatTime(log.timestamp)}</p>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#A1A1AA]">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredLogs.length)} of {filteredLogs.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
                className="rounded-md h-8 px-3"
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs font-medium text-[#52525B]">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                className="rounded-md h-8 px-3"
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
